import { lazy } from 'react';

// React.lazy() returns a NEW component type on every call, and React treats a
// new type as a different component — it unmounts the old subtree and mounts
// the new one from scratch. This function used to build fresh lazy types on
// every invocation, and Layout re-invokes it whenever `modulesConfigs` /
// `layouts` change identity, which happens on *every* config message coming
// from the Backoffice site editor. Result: each tiny config change remounted
// the whole page, the single <Suspense> fallback replaced the entire document
// for a tick, the document height collapsed and the browser reset the iframe
// scroll position to the top.
//
// Caching the lazy type per resolved module name keeps the component identity
// stable across config updates, so config changes are plain re-renders: no
// remount, no suspense flash, no scroll jump (and module state is preserved).
const lazyComponentsCache = new Map();

const getLazyComponent = (moduleName) => {
  const cached = lazyComponentsCache.get(moduleName);
  if (cached) {
    return cached;
  }

  const Component = lazy(() =>
    import(`../modules/${moduleName}/${moduleName}`).catch((err) => {
      console.error(`Failed to load component: ${moduleName}`, err);
      return import('../modules/LazyImportFallback/LazyImportFallback');
    })
  );

  lazyComponentsCache.set(moduleName, Component);
  return Component;
};

const importComponents = (pageComponents, componentConfigs) => {
  const appData = [];
  for (const property of pageComponents ?? []) {
    // resolved eagerly (it used to be resolved inside the lazy factory) so the
    // cache key and the imported path always match
    const moduleName = componentConfigs?.[property]?.module || property;

    appData.push({
      Component: getLazyComponent(moduleName || property),
      ComponentName: property,
    });
  }
  return appData;
};

export default importComponents;
